import { useMemo } from 'react'
import ActionItemList from './ActionItemList'
import ActionLogPanel from './ActionLogPanel'
import BsrTrendChart from './BsrTrendChart'
import ClusterCard from './ClusterCard'
import CreatorConvergenceAlert from './CreatorConvergenceAlert'
import CreatorRosterPanel from './CreatorRosterPanel'
import HashtagVelocityChart from './HashtagVelocityChart'
import KeywordOverlapTable from './KeywordOverlapTable'
import LivestreamFeed from './LivestreamFeed'
import { PredictionBadge } from './PredictionBadge'
import PurchaseIntentIndicator from './PurchaseIntentIndicator'
import ScoreHistoryChart from './ScoreHistoryChart'
import TikTokAdTracker from './TikTokAdTracker'
import {
  useActions,
  useBrief,
  useCompetitorKeywords,
  useCompetitors,
  useCreators,
  useLivestreams,
  usePredictions,
  useScoreBreakdown,
  useSignalClusters,
  useTikTokIntel,
} from '../../hooks/useCompetitiveData'
import { generateSignalSummary } from '../../lib/competitive-utils'

interface CompetitorDetailPanelProps {
  competitorId: number
  competitorName: string
  onBack: () => void
}

function normalizeScore(value: number | null | undefined): number {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return 0
  }
  return Number(value)
}

function urgencyMeta(urgency?: string): { label: string; classes: string } {
  const normalized = String(urgency || '').toLowerCase()
  if (normalized === 'act_now') {
    return { label: 'Act Now', classes: 'border-red-200 bg-red-50 text-red-700' }
  }
  if (normalized === 'watch') {
    return { label: 'Watch', classes: 'border-amber-200 bg-amber-50 text-amber-700' }
  }
  return { label: 'Monitor', classes: 'border-slate-200 bg-slate-100 text-slate-700' }
}

function channelList(clusters: Array<{ primary_channel: string; signal_count: number }>) {
  const counts = new Map<string, number>()
  for (const cluster of clusters) {
    const channel = cluster.primary_channel
    counts.set(channel, (counts.get(channel) ?? 0) + Number(cluster.signal_count ?? 0))
  }

  return Array.from(counts.entries())
    .map(([channel, count]) => ({ channel, count }))
    .sort((a, b) => b.count - a.count)
}

function prettyChannel(channel: string): string {
  if (channel === 'google_shopping') return 'Google Shopping'
  if (channel === 'cross_platform') return 'Cross-Platform'
  if (channel === 'tiktok') return 'TikTok'
  if (channel === 'amazon') return 'Amazon'
  return channel
}

interface ScoreLayers {
  anomaly_layer: number
  continuous_layer: number
  predictive_layer: number
}

function normalizeLayer(value: number): number {
  if (!Number.isFinite(value)) {
    return 0
  }
  return Math.max(0, value)
}

function layerContextText(layers: ScoreLayers): string | null {
  const anomaly = layers.anomaly_layer
  const continuous = layers.continuous_layer

  if (anomaly > 20 && continuous > 20) {
    return 'Combined threat — both spike AND sustained'
  }
  if (anomaly > 25 && continuous < 10) {
    return 'Spike event — likely promotional or one-time'
  }
  if (continuous > 25 && anomaly < 10) {
    return 'Sustained buildup — more concerning long-term'
  }
  return null
}

function formatRelativeTime(value: string | null | undefined): string {
  if (!value) {
    return 'Unknown'
  }
  const timestamp = new Date(value).getTime()
  if (!Number.isFinite(timestamp)) {
    return 'Unknown'
  }

  const diffMs = Date.now() - timestamp
  if (diffMs < 60 * 60 * 1000) {
    return `${Math.max(1, Math.round(diffMs / (60 * 1000)))}m ago`
  }
  if (diffMs < 24 * 60 * 60 * 1000) {
    return `${Math.max(1, Math.round(diffMs / (60 * 60 * 1000)))}h ago`
  }
  return `${Math.max(1, Math.round(diffMs / (24 * 60 * 60 * 1000)))}d ago`
}

function firstActionText(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    if (value && value.trim()) {
      return value.trim()
    }
  }
  return null
}

function parseFirstNumber(text: string, pattern: RegExp): number | null {
  const match = text.match(pattern)
  if (!match || !match[1]) {
    return null
  }
  const parsed = Number(match[1].replace(/,/g, ''))
  return Number.isFinite(parsed) ? parsed : null
}

function extractPaidDisclosureRate(text: string): number | null {
  return parseFirstNumber(text, /paid disclosure rate[=:]?\s*([\d.]+)%/i)
}

function extractCreatorStats(text: string): { mentions: number | null; creators: number | null } {
  const detailed = text.match(/(\d+)\s*mentions?\s*from\s*(\d+)\s*unique/i)
  if (detailed?.[1] && detailed?.[2]) {
    return {
      mentions: Number(detailed[1]),
      creators: Number(detailed[2]),
    }
  }
  return {
    mentions: null,
    creators: parseFirstNumber(text, /(\d+)\s*(?:macro\/mega|macro|mega|unique)?\s*creators?/i),
  }
}

function extractBsrMove(text: string): { from: number | null; to: number | null } {
  const directional = text.match(/#?([\d,]+)\s*(?:→|->|to)\s*#?([\d,]+)/i)
  if (directional?.[1] && directional?.[2]) {
    return {
      from: Number(directional[1].replace(/,/g, '')),
      to: Number(directional[2].replace(/,/g, '')),
    }
  }
  return {
    from: parseFirstNumber(text, /baseline\s*#?([\d,]+)/i),
    to: parseFirstNumber(text, /current\s*#?([\d,]+)/i),
  }
}

function threatLevel(score: number): string {
  if (score >= 70) return 'HIGH'
  if (score >= 40) return 'MODERATE'
  if (score >= 20) return 'WATCH'
  return 'LOW'
}

export default function CompetitorDetailPanel({ competitorId, competitorName, onBack }: CompetitorDetailPanelProps) {
  const { data: competitorsData, isLoading: isCompetitorsLoading, error: competitorsError } = useCompetitors()
  const { data: briefResponse } = useBrief()
  const { data: actionsResponse } = useActions(competitorName)
  const { data: clustersResponse, isLoading: isClustersLoading, error: clustersError } = useSignalClusters({ competitorId })
  const { data: keywordResponse } = useCompetitorKeywords(competitorId)
  const { data: tiktokIntelResponse } = useTikTokIntel()
  const { data: creatorsResponse } = useCreators(competitorId)
  const { data: livestreamsResponse } = useLivestreams(competitorId)
  const { data: scoreBreakdownResponse, isLoading: isScoreBreakdownLoading } = useScoreBreakdown(competitorId)
  const { predictions } = usePredictions()

  const competitor = useMemo(
    () => competitorsData?.competitors?.find((row) => row.id === competitorId) ?? null,
    [competitorId, competitorsData?.competitors]
  )

  const score = normalizeScore(competitor?.threat_score)
  const scoreHistory = competitor?.score_history ?? []
  const bsrTrend = competitor?.bsr_trend_30d ?? []
  const clusters = clustersResponse?.clusters ?? []

  const scoreDelta = useMemo(() => {
    if (scoreHistory.length < 2) return 0
    const first = normalizeScore(scoreHistory[0]?.total_score)
    const last = normalizeScore(scoreHistory[scoreHistory.length - 1]?.total_score)
    return Math.round(last - first)
  }, [scoreHistory])

  const threatEntry = useMemo(
    () => briefResponse?.brief?.top_threats?.find((threat) => threat.competitor_id === competitorId) ?? null,
    [briefResponse?.brief?.top_threats, competitorId]
  )

  const urgency = urgencyMeta(threatEntry?.urgency)
  const competitorActions = actionsResponse?.actions ?? []
  const pendingActions = competitorActions.filter((action) =>
    ['pending', 'running', 'in_progress'].includes(String(action.status || '').toLowerCase())
  ).length
  const completedActions = competitorActions.filter((action) =>
    ['completed', 'done'].includes(String(action.status || '').toLowerCase())
  ).length
  const lastCheckedAt = useMemo(() => {
    const timestamps = competitorActions
      .map((action) => action.executed_at || action.updated_at || action.created_at)
      .filter((value): value is string => Boolean(value))
      .map((value) => new Date(value).getTime())
      .filter((value) => Number.isFinite(value))
      .sort((a, b) => b - a)

    return timestamps.length > 0 ? new Date(timestamps[0]).toISOString() : null
  }, [competitorActions])

  const keywordOverlapCount = keywordResponse?.keywords?.length ?? 0

  const channelCounts = useMemo(() => channelList(clusters), [clusters])
  const competitorAdSignals = useMemo(
    () => (tiktokIntelResponse?.adSignals ?? []).filter((row) => row.competitor_id === competitorId),
    [competitorId, tiktokIntelResponse?.adSignals]
  )
  const competitorIntentVideos = useMemo(
    () => (tiktokIntelResponse?.purchaseIntentVideos ?? []).filter((row) => row.competitor_id === competitorId),
    [competitorId, tiktokIntelResponse?.purchaseIntentVideos]
  )
  const competitorHashtagBreakouts = useMemo(
    () => (tiktokIntelResponse?.hashtagBreakouts ?? []).filter((row) => row.competitor_id === competitorId),
    [competitorId, tiktokIntelResponse?.hashtagBreakouts]
  )
  const creatorRows = creatorsResponse?.creators ?? []
  const convergenceAlerts = creatorsResponse?.convergenceAlerts ?? []
  const topConvergenceAlert = convergenceAlerts[0] ?? null
  const competitorLivestreams = livestreamsResponse?.livestreams ?? []
  const prediction = useMemo(
    () => predictions.find((row) => row.competitor_id === competitorId) ?? null,
    [competitorId, predictions]
  )
  const predictionFeatureRows = useMemo(() => {
    const features = prediction?.features_used ?? {}
    return [
      { key: 'bsr_momentum', label: 'BSR momentum' },
      { key: 'tiktok_momentum', label: 'TikTok momentum' },
      { key: 'event_density', label: 'Event density' },
      { key: 'creator_convergence', label: 'Creator convergence' },
      { key: 'anomaly_frequency', label: 'Anomaly frequency' },
      { key: 'current_score', label: 'Current score' },
    ].map(({ key, label }) => {
      const rawValue = Number(features[key] ?? 0)
      const value = Number.isFinite(rawValue) ? Math.max(0, Math.min(1, rawValue)) : 0
      return { key, label, value }
    })
  }, [prediction?.features_used])
  const predictionComputedAt = prediction?.computed_at
    ? new Date(prediction.computed_at).toLocaleString('en-US')
    : null
  const scoreLayers = useMemo<ScoreLayers>(() => {
    const layers = scoreBreakdownResponse?.breakdown?.[0]?.layers
    return {
      anomaly_layer: normalizeLayer(normalizeScore(layers?.anomaly_layer)),
      continuous_layer: normalizeLayer(normalizeScore(layers?.continuous_layer)),
      predictive_layer: normalizeLayer(normalizeScore(layers?.predictive_layer)),
    }
  }, [scoreBreakdownResponse?.breakdown])

  const errorMessage = competitorsError instanceof Error ? competitorsError.message : null
  const clustersErrorMessage = clustersError instanceof Error ? clustersError.message : null
  const topSignalText = firstActionText(competitor?.top_signal_summary, threatEntry?.headline, threatEntry?.detail) || ''
  const cleanedSignal = (topSignalText || '')
    .replace(/@[\w.]+/g, '')
    .replace(/[\w._]+,\s*[\w._]+,/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
  const topSignalBsr = extractBsrMove(cleanedSignal)
  const creatorActionFinding = useMemo(
    () =>
      competitorActions.find(
        (action) =>
          String(action.action_type || '').toLowerCase().includes('creator') &&
          firstActionText(action.finding, action.action_description)
      ) || null,
    [competitorActions]
  )
  const creatorActionText = firstActionText(
    creatorActionFinding?.finding,
    creatorActionFinding?.action_description
  ) || ''
  const creatorStats = extractCreatorStats(creatorActionText)
  const paidDisclosureRate = extractPaidDisclosureRate(creatorActionText)
  const creatorCount =
    creatorStats.creators ??
    (topConvergenceAlert?.creator_count && Number.isFinite(topConvergenceAlert.creator_count)
      ? Number(topConvergenceAlert.creator_count)
      : null)
  const threatEntryLatestBsr = (threatEntry as { latest_bsr?: number | null } | null)?.latest_bsr ?? null
  const latestBsr = useMemo(() => {
    const values = [
      Number(competitor?.latest_bsr_overall ?? Number.NaN),
      Number(threatEntryLatestBsr ?? Number.NaN),
    ]
    const resolved = values.find((value) => Number.isFinite(value) && value > 0)
    return resolved ?? null
  }, [competitor?.latest_bsr_overall, threatEntryLatestBsr])
  const previousBsr = useMemo(() => {
    const values = (competitor?.bsr_trend_30d ?? [])
      .map((point) => Number(point.avg_bsr))
      .filter((value) => Number.isFinite(value) && value > 0)
    if (values.length >= 2) {
      return values[values.length - 2]
    }
    return null
  }, [competitor?.bsr_trend_30d])
  const topSignalSummary = useMemo(
    () =>
      generateSignalSummary(
        topSignalText,
        creatorCount ?? 0,
        paidDisclosureRate,
        latestBsr,
        previousBsr,
        competitor?.brand_name ?? competitorName
      ),
    [competitor?.brand_name, competitorName, creatorCount, latestBsr, paidDisclosureRate, previousBsr, topSignalText]
  )

  const predictionPercent = prediction ? Math.round(prediction.probability_top5_90d * 100) : null
  const topPredictionDrivers = predictionFeatureRows
    .filter((feature) => feature.value > 0)
    .sort((left, right) => right.value - left.value)
    .slice(0, 2)
    .map((feature) => `${feature.label} (${Math.round(feature.value * 100)}%)`)

  const storyText = useMemo(() => {
    const name = competitor?.brand_name ?? competitorName
    const sentences: string[] = []

    sentences.push(`${name} is a ${threatLevel(score).toLowerCase()} competitive threat this week.`)

    if (topSignalSummary) {
      sentences.push(topSignalSummary)
    }

    if (predictionPercent !== null) {
      sentences.push(
        `Model outlook: ${predictionPercent}% chance of breaking into Buttah's Amazon top-5 competitor set within 90 days.`
      )
    }

    return sentences.slice(0, 4).join(' ')
  }, [
    competitor?.brand_name,
    competitorName,
    predictionPercent,
    score,
    topSignalSummary,
  ])

  const scoreExplanation = useMemo(() => {
    const parts: string[] = []
    const threatEntryData = (threatEntry || {}) as {
      total_score?: number | null
      creator_count?: number | null
      latest_bsr?: number | null
      score_delta_7d?: number | null
    }
    const competitorData = (competitor || {}) as {
      threat_score?: number | null
      creator_mentions_7d?: number | null
      paid_disclosure_rate?: number | null
      latest_bsr?: number | null
      bsr_delta_7d?: number | null
      score_delta_7d?: number | null
    }

    const score = competitorData.threat_score ?? threatEntryData.total_score ?? 0

    const creatorCountValue = threatEntryData.creator_count ?? competitorData.creator_mentions_7d ?? 0
    if (creatorCountValue > 0) {
      parts.push(`${creatorCountValue} TikTok creators mentioned this brand in the last 7 days`)
    }

    const paidRate = competitorData.paid_disclosure_rate ?? null
    if (paidRate !== null) {
      if (paidRate < 5) {
        parts.push(`only ${paidRate.toFixed(1)}% of creator posts are paid (organic buzz)`)
      } else {
        parts.push(`${paidRate.toFixed(1)}% of creator posts are paid (active campaign)`)
      }
    }

    const bsr = competitorData.latest_bsr ?? threatEntryData.latest_bsr
    const bsrDelta = competitorData.bsr_delta_7d ?? null
    if (bsr) {
      if (bsrDelta && bsrDelta < 0) {
        parts.push(`Amazon BSR improved from #${(bsr - bsrDelta).toLocaleString()} to #${bsr.toLocaleString()} this week`)
      } else if (bsr < 15000) {
        parts.push(`Amazon BSR is #${bsr.toLocaleString()} (active seller)`)
      }
    }

    const delta = threatEntryData.score_delta_7d ?? competitorData.score_delta_7d ?? null
    if (delta !== null && delta !== 0) {
      parts.push(`threat score ${delta > 0 ? 'rose' : 'dropped'} ${Math.abs(delta)} points this week`)
    }

    if (parts.length === 0) {
      return score < 10
        ? 'Low activity across all monitored channels. No significant signals detected.'
        : 'Score reflects aggregate signals across TikTok, Amazon, and keyword monitoring.'
    }

    return `Score of ${score} is driven by: ${parts.join('; ')}.`
  }, [competitor, threatEntry])

  const keySignalBullets = useMemo(() => {
    const bullets: string[] = []

    if (topSignalBsr.from && topSignalBsr.to && topSignalBsr.to < topSignalBsr.from) {
      bullets.push(
        `Amazon rank improved from #${topSignalBsr.from.toLocaleString('en-US')} to #${topSignalBsr.to.toLocaleString('en-US')} — likely stronger weekly unit movement.`
      )
    }

    if (creatorCount || cleanedSignal.toLowerCase().includes('tiktok')) {
      const creatorLabel = creatorCount
        ? `${creatorCount.toLocaleString('en-US')} creators`
        : 'Elevated creator volume'
      const paidLabel =
        paidDisclosureRate === null
          ? ''
          : paidDisclosureRate < 2
            ? 'This appears mostly organic (<2% paid tags).'
            : `Paid-tag rate is ${paidDisclosureRate.toFixed(1)}%.`
      bullets.push(`TikTok momentum: ${creatorLabel} active in the last 7 days. ${paidLabel}`.trim())
    }

    if (bullets.length === 0 && cleanedSignal) {
      bullets.push(cleanedSignal.replace(/\s+/g, ' ').trim())
    }

    return bullets.slice(0, 3)
  }, [creatorCount, cleanedSignal, paidDisclosureRate, topSignalBsr.from, topSignalBsr.to])

  const recommendedActions = useMemo(() => {
    const actions: string[] = []
    const creatorActionCompleted = creatorActionFinding
      ? ['completed', 'done'].includes(String(creatorActionFinding.status || '').toLowerCase())
      : false

    if (creatorActionFinding) {
      const verdict =
        paidDisclosureRate === null
          ? 'results captured'
          : paidDisclosureRate < 2
            ? 'verdict: organic buzz'
            : 'verdict: paid campaign'
      actions.push(
        `${creatorActionCompleted ? 'Completed' : 'Running'} Creator Scan — ${verdict}.`
      )
    }

    if (topSignalBsr.from && topSignalBsr.to && topSignalBsr.to < topSignalBsr.from) {
      actions.push('Watch Amazon listing changes (price, coupon, Subscribe & Save) and counter fast if promotion pressure persists.')
    }

    if (creatorCount || cleanedSignal.toLowerCase().includes('tiktok')) {
      actions.push('Activate overlapping creators this week to defend share of voice on melanin-rich skincare intents.')
    }

    if (threatEntry?.recommended_actions?.length) {
      for (const action of threatEntry.recommended_actions) {
        if (actions.length >= 3) break
        if (!action || !action.trim()) continue
        const lowered = action.toLowerCase()
        if (creatorActionCompleted && /check if.*paid|run paid.*classification|check.*creator campaign|classify.*paid/i.test(lowered)) {
          continue
        }
        actions.push(action.trim())
      }
    }

    return actions.slice(0, 3)
  }, [
    creatorActionFinding,
    creatorCount,
    cleanedSignal,
    paidDisclosureRate,
    threatEntry?.recommended_actions,
    topSignalBsr.from,
    topSignalBsr.to,
  ])

  return (
    <section className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
      >
        ← Back to Competitors
      </button>

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{competitor?.brand_name ?? competitorName}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${urgency.classes}`}>
                {urgency.label}
              </span>
              {pendingActions > 0 && (
                <span className="inline-flex rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                  {pendingActions} pending action{pendingActions === 1 ? '' : 's'}
                </span>
              )}
              {keywordOverlapCount > 0 && (
                <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
                  {keywordOverlapCount} overlap keyword{keywordOverlapCount === 1 ? '' : 's'}
                </span>
              )}
            </div>
          </div>

          <div className="text-right">
            <div className="text-3xl font-bold text-slate-900">{Math.round(score)}</div>
            <div className={`text-sm font-semibold ${scoreDelta >= 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {scoreDelta >= 0 ? '+' : ''}
              {scoreDelta} delta
            </div>
          </div>
        </div>

        <div className="mt-4 border-t border-slate-200 pt-4">
          <p className="text-sm leading-relaxed text-slate-700">
            {storyText || `${competitor?.brand_name ?? competitorName} is currently being monitored with no dominant threat signal.`}
          </p>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-3 text-xs text-slate-600">
          <p className="font-semibold text-slate-700">
            ✅ {completedActions} actions completed · {pendingActions} pending
          </p>
          <p>
            Last checked: {formatRelativeTime(lastCheckedAt)}
          </p>
        </div>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Automated Actions</h3>
        <ActionLogPanel competitorName={competitor?.brand_name ?? competitorName} />
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Why This Score</h3>

        {isScoreBreakdownLoading ? (
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
            Loading risk explanation...
          </div>
        ) : (
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            <p>{scoreExplanation}</p>
            <div className="mt-2 text-xs text-slate-600">
              {layerContextText(scoreLayers) || ''}
            </div>
          </div>
        )}
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Competitive Situation</h3>

        <div className="mt-3 space-y-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Threat Level
            </p>
            <p className="mt-1 text-sm text-slate-700">
              {threatLevel(score)} ({Math.round(score)} / 100){' '}
              {scoreDelta !== 0 ? `· ${scoreDelta > 0 ? '+' : ''}${scoreDelta} this week` : '· stable vs last week'}
            </p>
          </div>

          {keySignalBullets.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Key Signals This Week</p>
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                {keySignalBullets.map((bullet, index) => (
                  <li key={`${bullet}-${index}`}>• {bullet}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Prediction</p>
            {predictionPercent !== null ? (
              <p className="mt-1 text-sm text-slate-700">
                {predictionPercent}% chance this competitor enters Buttah&apos;s top-5 Amazon competitor set in 90 days.
                {topPredictionDrivers.length > 0 ? ` Main drivers: ${topPredictionDrivers.join(', ')}.` : ''}
              </p>
            ) : (
              <p className="mt-1 text-sm text-slate-700">No prediction model output yet.</p>
            )}
          </div>

          {recommendedActions.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Recommended Actions</p>
              <ol className="mt-2 space-y-1 text-sm text-slate-700">
                {recommendedActions.map((action, index) => (
                  <li key={`${action}-${index}`}>{index + 1}. {action}</li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Prediction</h3>

        {prediction ? (
          <div className="mt-3 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <PredictionBadge probability={prediction.probability_top5_90d} />
              <div className="text-xs text-slate-500">
                {prediction.model_version}
                {predictionComputedAt ? ` • ${predictionComputedAt}` : ''}
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              {topPredictionDrivers.length > 0
                ? `Main momentum drivers right now: ${topPredictionDrivers.join(', ')}.`
                : 'Model output is available, but feature attribution is limited right now.'}
            </div>
          </div>
        ) : (
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
            No prediction data yet.
          </div>
        )}
      </section>

      <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">TikTok Intel</h3>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <TikTokAdTracker
            adSignals={competitorAdSignals.slice(0, 8)}
            title="Paid TikTok Content"
            emptyMessage="No paid TikTok ads detected for this competitor."
          />

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Top Purchase-Intent Videos</h4>
            {competitorIntentVideos.length === 0 ? (
              <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                No high purchase-intent TikTok videos in the last 30 days.
              </div>
            ) : (
              <div className="mt-3 space-y-3">
                {competitorIntentVideos.slice(0, 5).map((video) => (
                  <article key={video.id} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                    <PurchaseIntentIndicator score={video.purchase_intent_score} compact />
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-600">
                      <span>Views: {Math.round(video.view_count || 0).toLocaleString('en-US')}</span>
                      <span>Likes: {Math.round(video.like_count || 0).toLocaleString('en-US')}</span>
                    </div>
                    {video.content_url && (
                      <a
                        href={video.content_url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-flex text-xs font-semibold text-[var(--patterniq-navy-900)] hover:underline"
                      >
                        Open TikTok video
                      </a>
                    )}
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>

        <HashtagVelocityChart
          breakouts={competitorHashtagBreakouts.slice(0, 8)}
          title="Competitor Hashtag Breakouts"
          emptyMessage="No breakout hashtags captured for this competitor."
        />
      </section>

      <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Creators</h3>

        {topConvergenceAlert && (
          <CreatorConvergenceAlert
            competitorName={topConvergenceAlert.competitor_name}
            creatorCount={topConvergenceAlert.creator_count}
            timeWindowDays={7}
          />
        )}

        {creatorActionText && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Creator Scan Results</p>
            <p className="mt-1">
              {creatorStats.creators
                ? `${creatorStats.creators.toLocaleString('en-US')} unique creators mentioned this competitor this week`
                : 'Creator activity is elevated this week'}
              {creatorStats.mentions ? ` (${creatorStats.mentions.toLocaleString('en-US')} total mentions)` : ''}.
            </p>
            <p className="mt-1">
              {paidDisclosureRate === null
                ? 'Paid disclosure rate was not captured in this run.'
                : paidDisclosureRate < 1
                  ? 'Verdict: this looks organic — less than 1% of mentions include paid tags.'
                  : paidDisclosureRate < 5
                    ? `Verdict: mostly organic — ${paidDisclosureRate.toFixed(1)}% include paid tags.`
                    : `Verdict: campaign support detected — ${paidDisclosureRate.toFixed(1)}% include paid tags.`}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <CreatorRosterPanel
            creators={creatorRows}
            title="Creator Mention Roster"
            emptyMessage="No creators have mentioned this competitor in the last 30 days."
          />
          <LivestreamFeed
            livestreams={competitorLivestreams}
            title="Competitor Livestream Feed"
            emptyMessage="No livestream events detected in the last 30 days"
          />
        </div>
      </section>

      <ActionItemList competitorId={competitorId} />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <ScoreHistoryChart
          history={scoreHistory.map((point) => ({ date: point.score_date, score: Number(point.total_score ?? 0) }))}
          competitorName={competitor?.brand_name ?? competitorName}
        />
        <BsrTrendChart
          bsrTrend={bsrTrend.map((point) => ({ date: point.day, bsr: Number(point.avg_bsr ?? 0) }))}
          competitorName={competitor?.brand_name ?? competitorName}
        />
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Signal Count by Channel</p>
          {channelCounts.length === 0 ? (
            <p className="mt-3 text-sm text-slate-600">No clustered channel counts available.</p>
          ) : (
            <ul className="mt-3 space-y-1 text-sm text-slate-700">
              {channelCounts.map((row) => (
                <li key={row.channel} className="flex items-center justify-between gap-3">
                  <span>{prettyChannel(row.channel)}</span>
                  <span className="font-semibold text-slate-900">{row.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Signal Timeline</h3>

        {clustersErrorMessage && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{clustersErrorMessage}</div>
        )}

        {isClustersLoading ? (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">Loading clustered signals...</div>
        ) : clusters.length === 0 ? (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
            No clustered signals available for this competitor yet.
          </div>
        ) : (
          <div className="space-y-3">
            {clusters.map((cluster) => (
              <ClusterCard key={cluster.id} cluster={cluster} />
            ))}
          </div>
        )}
      </section>

      <KeywordOverlapTable competitorId={competitorId} />

      {isCompetitorsLoading && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">Loading competitor profile...</div>
      )}
      {errorMessage && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{errorMessage}</div>
      )}
    </section>
  )
}
